import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SUPABASE_BUCKET_NAME } from '../config/supabase.config';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

export default function ImagePreview() {
  const { dark } = useTheme();
  const { id } = useParams<{ id: string }>();
  const [pictureUrl, setPictureUrl] = useState<string>('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPicture = async () => {
      try {
        if (!id) {
          setError('Invalid story ID');
          return;
        }
        const { data, error } = await supabase
          .from('stories')
          .select('picture')
          .eq('id', id)
          .single();
        if (error || !data) {
          setError('Failed to fetch image');
          return;
        }
        let filePath = data.picture.replace(/^\/+/, '').replace(/\/{2,}/g, '');
        // Remove any encoded "\x" patterns and leading "x"
        filePath = filePath.replace(/^\\?x/, '');
        // Helper: convert hex string to ASCII
        const hexToAscii = (hex: string): string => {
          let str = '';
          for (let i = 0; i < hex.length; i += 2) {
            str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
          }
          return str;
        };
        if (/^[0-9a-f]+$/i.test(filePath)) {
          filePath = hexToAscii(filePath);
        }
        const { data: forcedSignedData, error: forcedSignedError } = await supabase
          .storage
          .from(SUPABASE_BUCKET_NAME)
          .createSignedUrl(filePath, 3600);
        if (forcedSignedError) {
          setError('Error creating preview URL');
        } else if (forcedSignedData?.signedUrl) {
          setPictureUrl(forcedSignedData.signedUrl);
        }
      } catch (err) {
        setError('Error fetching image preview');
      }
    };

    fetchPicture();
  }, [id]);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-[#f6faff] dark:bg-[#181825] overflow-hidden">
      {/* Playful, theme-adaptive blob background */}
      {pictureUrl && (
        <>
          {dark ? (
            <svg className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] opacity-30 z-0 animate-blob-float" viewBox="0 0 600 300" fill="none"><ellipse cx="300" cy="150" rx="280" ry="110" fill="#b4befe" /></svg>
          ) : (
            <svg className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] opacity-30 z-0 animate-blob-float" viewBox="0 0 600 300" fill="none"><ellipse cx="300" cy="150" rx="280" ry="110" fill="#f9e2af" /></svg>
          )}
        </>
      )}
      <div className="max-w-4xl mx-auto mt-20 w-full flex flex-col items-center z-10">
        <Link to={id ? `/story/${btoa(id)}` : "/story"} className="text-blue-400 hover:underline mb-6 font-bold text-lg">
          Back to Story
        </Link>
        {error ? (
          <p className="text-red-500 mt-4">{error}</p>
        ) : pictureUrl ? (
          <img
            src={pictureUrl}
            alt="Full Preview"
            className="w-full max-w-2xl h-auto object-contain mt-4 rounded-3xl shadow-2xl border-4 border-white/30"
            style={{ background: 'rgba(255,255,255,0.7)' }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center mt-12">
            <span className="text-5xl animate-bounce mb-2">🖼️</span>
            <div className="w-40 h-40 rounded-3xl bg-gradient-to-br from-[#b4befe]/30 to-[#f9e2af]/30 dark:from-[#b4befe]/40 dark:to-[#a6e3a1]/30 animate-pulse flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 animate-shine rounded-3xl" style={{background: 'linear-gradient(90deg, #fff0 0%, #fff8 50%, #fff0 100%)', backgroundSize: '200px 100%'}} />
              <span className="text-3xl opacity-60 animate-spin-slow">🖼️</span>
            </div>
            <span className="text-[#a6adc8] dark:text-[#fab387] animate-pulse font-semibold text-sm mt-2">Loading image preview...</span>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
